import {
  DragHandleIcon,
  InfoIcon,
  SettingsIcon
} from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { useState } from 'react';
import { ColumnConfig } from '../../hooks/useColumnManagement';

interface ColumnManagementProps<T = unknown> {
  columns: ColumnConfig<T>[];
  visibleColumns: ColumnConfig<T>[];
  hiddenColumns: ColumnConfig<T>[];
  onToggleVisibility: (columnId: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onReset: () => void;
}

const ColumnManagement = <T = unknown>({
  visibleColumns,
  hiddenColumns,
  onToggleVisibility,
  onReorder,
  onReset,
}: ColumnManagementProps<T>) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showHelp, setShowHelp] = useState(false);

  const buttonBg = useColorModeValue(
    'background.secondary',
    'gray.700'
  );
  const buttonHoverBg = useColorModeValue('gray.300', 'gray.600');
  const modalBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const fromIndex = result.source.index;
    const toIndex = result.destination.index;

    // Only allow reordering within visible columns
    if (result.source.droppableId === 'visible-columns') {
      onReorder(fromIndex, toIndex);
    }
  };

  const handleReset = () => {
    onReset();
    onClose();
  };

  return (
    <>
      <Tooltip
        label="Customize table columns"
        placement="bottom"
        hasArrow
        bg="gray.700"
        color="white"
        fontSize="0.75rem"
      >
        <IconButton
          aria-label="Column settings"
          icon={<SettingsIcon />}
          bg={buttonBg}
          mr="0.5rem"
          height="2.5rem"
          width="2.5rem"
          _hover={{
            bg: buttonHoverBg,
            transform: 'scale(1.05)',
            transition: 'all 0.2s ease-in-out',
          }}
          _active={{
            bg: buttonHoverBg,
            transform: 'scale(0.95)',
          }}
          borderRadius="0.375rem"
          border="none"
          size="sm"
          onClick={onOpen}
          transition="all 0.2s ease-in-out"
        />
      </Tooltip>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        motionPreset="slideInBottom"
        scrollBehavior="inside"
      >
        <ModalOverlay
          bg="blackAlpha.300"
          backdropFilter="blur(10px)"
          transition="opacity 0.2s ease-in-out"
        />
        <ModalContent
          bg={modalBg}
          borderRadius="1rem"
          boxShadow="2xl"
          maxH="90vh"
          transition="all 0.3s ease-in-out"
          _focus={{ boxShadow: '2xl' }}
        >
          <ModalHeader
            pb="0.5rem"
            borderBottom="1px solid"
            borderColor={borderColor}
          >
            <Flex justify="space-between" align="center">
              <HStack spacing="0.75rem">
                <Icon as={SettingsIcon} color="blue.500" />
                <Text
                  fontSize="1.25rem"
                  fontWeight="700"
                  color="gray.700"
                >
                  Table Columns
                </Text>
                <Badge
                  colorScheme="blue"
                  variant="subtle"
                  fontSize="0.625rem"
                >
                  {visibleColumns.length} visible
                </Badge>
              </HStack>
              <HStack spacing="0.5rem">
                <Tooltip label="Show help" placement="bottom">
                  <IconButton
                    aria-label="Show help"
                    icon={<InfoIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowHelp(!showHelp)}
                    color="gray.500"
                    _hover={{ color: 'blue.500' }}
                  />
                </Tooltip>
                <ModalCloseButton position="static" />
              </HStack>
            </Flex>
          </ModalHeader>

          <ModalBody p="1.5rem">
            <VStack align="stretch" spacing="1.5rem">
              {/* Help Section */}
              <Collapse in={showHelp} animateOpacity>
                <Alert
                  status="info"
                  borderRadius="0.75rem"
                  variant="subtle"
                >
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="0.875rem">
                      How to customize columns:
                    </AlertTitle>
                    <AlertDescription fontSize="0.75rem">
                      • Check/uncheck columns to show/hide them
                      <br />
                      • Drag visible columns to reorder them
                      <br />• Changes are automatically saved
                    </AlertDescription>
                  </Box>
                </Alert>
              </Collapse>

              {/* Action Buttons */}
              <Flex justify="space-between" align="center">
                <HStack spacing="0.75rem">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color="gray.700"
                  >
                    Manage your table view
                  </Text>
                  <Badge
                    colorScheme="green"
                    variant="subtle"
                    fontSize="0.625rem"
                  >
                    {hiddenColumns.length} hidden
                  </Badge>
                </HStack>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReset}
                  fontSize="0.75rem"
                  _hover={{
                    bg: 'red.50',
                    borderColor: 'red.300',
                    color: 'red.600',
                  }}
                  transition="all 0.2s ease-in-out"
                >
                  Reset to Default
                </Button>
              </Flex>

              {/* Visible Columns Section */}
              <Box>
                <HStack
                  justify="space-between"
                  align="center"
                  mb="0.75rem"
                >
                  <HStack spacing="0.5rem">
                    <Text
                      fontSize="0.875rem"
                      fontWeight="600"
                      color="gray.700"
                    >
                      Visible Columns
                    </Text>
                    <Tooltip label="Drag to reorder" placement="top">
                      <Icon
                        as={DragHandleIcon}
                        color="gray.400"
                        boxSize="0.875rem"
                      />
                    </Tooltip>
                  </HStack>
                  <Text fontSize="0.75rem" color="gray.500">
                    {visibleColumns.length} columns
                  </Text>
                </HStack>

                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="visible-columns">
                    {(provided, snapshot) => (
                      <Box
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        bg={
                          snapshot.isDraggingOver
                            ? 'blue.50'
                            : 'gray.50'
                        }
                        borderRadius="0.75rem"
                        p="0.75rem"
                        minH="8rem"
                        maxH="16rem"
                        overflowY="auto"
                        border="2px dashed"
                        borderColor={
                          snapshot.isDraggingOver
                            ? 'blue.300'
                            : 'transparent'
                        }
                        transition="all 0.2s ease-in-out"
                      >
                        <VStack align="stretch" spacing="0.5rem">
                          {visibleColumns.map((column, index) => (
                            <Draggable
                              key={column.id}
                              draggableId={column.id}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  bg={
                                    snapshot.isDragging
                                      ? 'white'
                                      : 'transparent'
                                  }
                                  borderRadius="0.5rem"
                                  border="1px solid"
                                  borderColor={
                                    snapshot.isDragging
                                      ? 'blue.300'
                                      : 'transparent'
                                  }
                                  boxShadow={
                                    snapshot.isDragging
                                      ? 'lg'
                                      : 'none'
                                  }
                                  transform={
                                    snapshot.isDragging
                                      ? 'rotate(2deg)'
                                      : 'none'
                                  }
                                  transition="all 0.2s ease-in-out"
                                >
                                  <Flex
                                    align="center"
                                    p="0.75rem"
                                    cursor="grab"
                                    _hover={{
                                      bg: 'white',
                                      transform: 'translateY(-1px)',
                                      boxShadow: 'sm',
                                    }}
                                    transition="all 0.2s ease-in-out"
                                    {...provided.dragHandleProps}
                                  >
                                    <Checkbox
                                      isChecked={column.isVisible}
                                      onChange={() =>
                                        onToggleVisibility(column.id)
                                      }
                                      mr="0.75rem"
                                      size="md"
                                      colorScheme="blue"
                                    />
                                    <Text
                                      fontSize="0.875rem"
                                      fontWeight="500"
                                      flex="1"
                                    >
                                      {column.header}
                                    </Text>
                                    <HStack spacing="0.5rem">
                                      <Badge
                                        colorScheme="green"
                                        variant="subtle"
                                        fontSize="0.625rem"
                                      >
                                        Visible
                                      </Badge>
                                      <Icon
                                        as={DragHandleIcon}
                                        color="gray.400"
                                        boxSize="1rem"
                                        _hover={{ color: 'gray.600' }}
                                      />
                                    </HStack>
                                  </Flex>
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </VStack>
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              </Box>

              {/* Hidden Columns Section */}
              {hiddenColumns.length > 0 && (
                <Box>
                  <HStack
                    justify="space-between"
                    align="center"
                    mb="0.75rem"
                  >
                    <Text
                      fontSize="0.875rem"
                      fontWeight="600"
                      color="gray.700"
                    >
                      Hidden Columns
                    </Text>
                    <Text fontSize="0.75rem" color="gray.500">
                      {hiddenColumns.length} columns
                    </Text>
                  </HStack>

                  <Box
                    bg="gray.50"
                    borderRadius="0.75rem"
                    p="0.75rem"
                    maxH="12rem"
                    overflowY="auto"
                  >
                    <VStack align="stretch" spacing="0.5rem">
                      {hiddenColumns.map((column) => (
                        <Flex
                          key={column.id}
                          align="center"
                          p="0.75rem"
                          bg="white"
                          borderRadius="0.5rem"
                          border="1px solid"
                          borderColor="gray.200"
                          _hover={{
                            bg: 'blue.50',
                            borderColor: 'blue.200',
                            transform: 'translateY(-1px)',
                            boxShadow: 'sm',
                          }}
                          transition="all 0.2s ease-in-out"
                        >
                          <Checkbox
                            isChecked={column.isVisible}
                            onChange={() =>
                              onToggleVisibility(column.id)
                            }
                            mr="0.75rem"
                            size="md"
                            colorScheme="blue"
                          />
                          <Text
                            fontSize="0.875rem"
                            fontWeight="500"
                            flex="1"
                          >
                            {column.header}
                          </Text>
                          <Badge
                            colorScheme="gray"
                            variant="subtle"
                            fontSize="0.625rem"
                          >
                            Hidden
                          </Badge>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Footer Actions */}
              <Divider />
              <Flex justify="space-between" align="center">
                <Text fontSize="0.75rem" color="gray.500">
                  Changes are automatically saved
                </Text>
                <HStack spacing="0.75rem">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onClose}
                    fontSize="0.75rem"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={onClose}
                    fontSize="0.75rem"
                    _hover={{ transform: 'translateY(-1px)' }}
                    transition="all 0.2s ease-in-out"
                  >
                    Done
                  </Button>
                </HStack>
              </Flex>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ColumnManagement;
